import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import EditShortcutBackButton from "@/components/edit-shortcut-back-button";
import styles from "@/components/edit-shortcut-popup.module.css";
import useCustomInput from "@/hooks/use-custom-input";
import useShortcuts from "@/hooks/use-shortcuts";
import * as signals from "@/signals";
import { EditShortcutPopupPage, FolderInfo } from "@/types";
import { handleKeyboardClick, isFolder } from "@/utils";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { FormEvent, JSX, useState } from "react";

interface EditShortcutFolderPageProps {
	isShown: boolean;
	selectedFolder: FolderInfo | null;
	setCurrentPage: (newValue: EditShortcutPopupPage) => void;
	setSelectedFolder: (newValue: FolderInfo | null) => void;
}

function EditShortcutFolderPage({
	isShown,
	selectedFolder,
	setCurrentPage,
	setSelectedFolder,
}: EditShortcutFolderPageProps): JSX.Element {
	const queryClient = useQueryClient();

	const [folderNameInputValue, setFolderNameInputValue] =
		useState<string>("");

	const { data } = useShortcuts();
	const folderNameInput = useCustomInput<HTMLInputElement>({
		setValue: setFolderNameInputValue,
	});

	const folderList: (FolderInfo | null)[] = [
		null,
		...(data ? data.children.filter(isFolder) : []),
	];
	const folderListElements = folderList.map((folder) => {
		const isChecked = selectedFolder?.id === folder?.id;

		const handleFolderOptionClick = (): void => {
			setCurrentPage(EditShortcutPopupPage.MAIN);
			setSelectedFolder(folder);
		};

		return (
			<div
				aria-checked={isChecked}
				className={styles["folder-option"]}
				key={folder ? folder.id : "none"}
				role="radio"
				tabIndex={0}
				onClick={handleFolderOptionClick}
				onKeyDown={handleKeyboardClick(handleFolderOptionClick)}
			>
				<span>{folder ? folder.folderName : t("none")}</span>
				{isChecked && (
					<FontAwesomeIcon
						icon={faCheck}
						size="lg"
					/>
				)}
			</div>
		);
	});

	const createFolder = async (
		event: FormEvent<HTMLFormElement>,
	): Promise<void> => {
		event.preventDefault();
		if (!folderNameInputValue) {
			return;
		}
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).createBookmarkFolder(
				folderNameInputValue,
			);
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	return (
		<div
			className={clsx(
				styles["page"],
				!isShown && styles["invisible"],
				isShown && styles["slid"],
			)}
		>
			<div className={styles["title-bar"]}>
				<EditShortcutBackButton setCurrentPage={setCurrentPage} />
				{t("selectShortcutFolder")}
			</div>
			<form
				className={styles["new-shortcut-folder-form"]}
				onSubmit={(event) => void createFolder(event)}
			>
				<input
					autoComplete="off"
					className="input"
					placeholder={t("folderName")}
					value={folderNameInputValue}
					{...folderNameInput}
				/>
				<button
					className={styles["submit-button"]}
					type="submit"
				>
					{t("create")}
				</button>
			</form>
			{folderListElements}
		</div>
	);
}

export default EditShortcutFolderPage;
